/* eslint-disable no-underscore-dangle */
import React, { useMemo, useState } from 'react';
import {
  Badge, Button, Card, Col, Form, Row, Table,
} from 'react-bootstrap';
import { getOrderLineKey } from '../../Payments/Components/orderLineKey';
import { SETTLED_PAYMENT_STATUSES } from '../../../Utils/paymentStatus';
// How a ledger line is NAMED — shared with the dispatcher's trip screen
// (Utils/orderLineKinds).
import {
  ORDER_LINE_KINDS as KIND_META,
  ORDER_LINE_KIND_ORDER as KIND_RENDER_ORDER,
  formatOrderLineRole as formatRole,
} from '../../../Utils/orderLineKinds';
import {
  bookingOperatorsById,
  describeLocks,
  isReversalLine,
  locksForLine,
  pairReversals,
  reversalReason,
} from '../../../Utils/orderLineAdjustments';

export function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  return `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * A booking's ledger as staff read it: the per-operator summary with the
 * booking's profit, every order line grouped by kind (a reversal folded into
 * the struck-through line it cancels, with its reason; a line a payment or
 * invoice references padlocked), what has been paid and is outstanding per
 * line on each side, and — once the payments are known — the two-sided
 * "still due / still to pay" panel.
 *
 * Presentational. Order Details' Order Ledger tab (OrderLedgerTab) fetches
 * `GET /bookings/order-lines` and hands it in with the booking's payments:
 *   - `order`               the booking (operators, currency, _id)
 *   - `lines`, `balanceByOperator`, `locks`  the order-lines response
 *   - `payments`            the booking's Payment rows, or null while they
 *                           are not known — the two-sided panel is then
 *                           left out, and the paid / outstanding cells say
 *                           so, rather than showing a €0.00 that reads as
 *                           "nothing paid"
 *   - `renderActions(line, lineLocks)`  the buttons for an adjustment row;
 *                           without it the table has no Actions column
 */
function OrderLedger({
  order, lines: orderLines = [], balanceByOperator = [], locks = {},
  payments: loadedPayments = null, renderActions = null,
}) {
  const payments = loadedPayments || [];
  // The booking's own unit. 2,299 bookings carry no `currency` at all.
  const bookingCurrency = order.currency || 'EUR';
  const [showOnlyAdjustments, setShowOnlyAdjustments] = useState(false);
  // Operator-side currency view: default 'settlement' (the operator's
  // own currency, e.g. HUF for an EverGreen Express driver paid in
  // forints) when it's unambiguous, fallback to 'booking' (EUR) when
  // the booking has operator lines in multiple currencies. The toggle
  // lets the operator-admin flip between the two.
  const [operatorCurrencyView, setOperatorCurrencyView] = useState('settlement');

  const operatorById = useMemo(() => bookingOperatorsById(order), [order]);

  const labelForOperatorLine = (line) => {
    const id = line.metadata?.operator_id;
    if (!id) return null;
    const op = operatorById.get(id);
    const role = formatRole(line.metadata?.role || op?.role);
    const name = op?.operator?.name;
    if (name && role) return `${name} · ${role}`;
    if (name) return name;
    if (role) return role;
    return `Operator ${id.slice(0, 6)}`;
  };

  const labelForServiceLine = (line) => {
    const name = line.metadata?.service_name || line.description;
    const qty = line.metadata?.quantity;
    if (qty && qty > 1) return `${name} × ${qty}`;
    return name;
  };

  // Group lines by kind for sectioned rendering. Untagged legacy lines
  // are bucketed as adjustments so pre-migration data still displays
  // reasonably until the backfill runs.
  const groups = useMemo(() => {
    const out = new Map(KIND_RENDER_ORDER.map((k) => [k, []]));
    orderLines.forEach((line, idx) => {
      const kind = line.kind && out.has(line.kind) ? line.kind : 'adjustment';
      out.get(kind).push({ ...line, _index: idx });
    });
    return out;
  }, [orderLines]);

  // Per-operator running totals: what we owe each operator and how much
  // cash they've already collected from the customer.
  //
  // CURRENCY RULE — each column keeps the units the money was agreed in.
  //
  // An operator's lines are NOT guaranteed to share a currency: a HUF
  // driver fee plus a EUR waiting-time adjustment is a normal shape
  // (PU_WEB_3600513). Converting the whole row to the booking currency
  // makes the columns addable but hides the 200,000 Ft we actually owe
  // the driver, and the raw alternative printed "+ 50 Ft" for a 50 EUR
  // adjustment and summed 200,000 Ft + 50 EUR as "200,050 Ft".
  //
  // So each BUCKET (assigned / adjustments / cash) is tracked per
  // currency and rendered in its own units when it holds just one —
  // Assigned stays "200,000 Ft", Adjustments stays "+ €50.00". A bucket
  // that itself mixes currencies falls back to the booking-currency sum
  // and is flagged, since there is nothing honest to print otherwise.
  //
  // `net` is the one figure that MUST be converted (Ft and EUR cannot be
  // added). It is reported in the operator's own settlement currency,
  // taken from the API's `balance_by_operator` — which already applied
  // the booking-date rate to each line — with the booking-currency
  // equivalent alongside. When the API gives us no settlement currency
  // for this operator we fall back to the booking-currency sum and say
  // so rather than inventing units.
  //
  // `order.currency` is read inline rather than via the `bookingCurrency`
  // const: that is declared further down, so this memo's body would hit
  // its temporal dead zone on first render.
  const perOperator = useMemo(() => {
    const bookingCur = order.currency || 'EUR';
    const settlementById = new Map(
      (balanceByOperator || [])
        .filter((b) => b && b.operator_id)
        .map((b) => [b.operator_id, b]),
    );

    const map = new Map();
    const emptyBucket = () => ({ byCurrency: new Map(), bc: 0, fxMissing: false });
    const upsert = (id, role) => {
      if (!map.has(id)) {
        const op = operatorById.get(id);
        map.set(id, {
          id,
          role: role || op?.role,
          name: op?.operator?.name,
          assigned: emptyBucket(),
          adjustments: emptyBucket(),
          cash: emptyBucket(),
        });
      }
      return map.get(id);
    };

    const raw = (l) => Number(l.credit_operator) || 0;
    const hasFx = (l) => l.credit_operator_in_booking_currency != null;
    const inBookingCurrency = (l) => (hasFx(l)
      ? Number(l.credit_operator_in_booking_currency)
      : Number(l.credit_operator) || 0);

    // `sign` flips cash, which is stored as a negative credit_operator.
    // Takes the owning entry + bucket name so it mutates our own object
    // rather than a parameter (eslint no-param-reassign).
    const addTo = (entry, bucketName, l, sign) => {
      const bucket = entry[bucketName];
      const amount = sign * raw(l);
      const cur = l.currency || bookingCur;
      const next = {
        byCurrency: bucket.byCurrency,
        bc: bucket.bc + sign * inBookingCurrency(l),
        // Older API responses predate the FX fields; without them a
        // mixed bucket cannot honestly claim a converted total.
        fxMissing: bucket.fxMissing || (Math.abs(amount) > 0.005 && !hasFx(l)),
      };
      if (Math.abs(amount) > 0.005) {
        next.byCurrency.set(cur, (next.byCurrency.get(cur) || 0) + amount);
      }
      // eslint-disable-next-line no-param-reassign
      entry[bucketName] = next;
    };

    // Effective booking-date rate for <settlement> per <booking currency>,
    // recovered from a line the API already converted. Derived rather than
    // re-fetched so the number shown is exactly the one the API applied.
    const rateFor = (settlementCur) => {
      if (!settlementCur || settlementCur === bookingCur) return null;
      const src = orderLines.find((l) => (l.currency || bookingCur) === settlementCur
        && Math.abs(Number(l.credit_operator) || 0) > 0.005
        && l.credit_operator_in_booking_currency != null
        && Math.abs(Number(l.credit_operator_in_booking_currency)) > 0.005);
      if (!src) return null;
      return Math.abs(
        Number(src.credit_operator) / Number(src.credit_operator_in_booking_currency),
      );
    };

    orderLines.forEach((line) => {
      const id = line.metadata?.operator_id;
      if (!id) return;
      const entry = upsert(id, line.metadata?.role);
      if (line.kind === 'operator') addTo(entry, 'assigned', line, 1);
      else if (line.kind === 'adjustment') addTo(entry, 'adjustments', line, 1);
      else if (line.kind === 'cash_from_client') addTo(entry, 'cash', line, -1);
    });

    // Resolve a bucket to { amount, currency, converted }. One currency ->
    // its own units. None -> a zero in the booking currency (renders as
    // "—"). Several -> the booking-currency sum, flagged.
    const resolve = (bucket) => {
      const entries = [...bucket.byCurrency.entries()];
      if (entries.length === 1) {
        return { amount: entries[0][1], currency: entries[0][0], converted: false };
      }
      const snap = Math.abs(bucket.bc) < 0.005 ? 0 : bucket.bc;
      return {
        amount: snap,
        currency: bookingCur,
        converted: entries.length > 1 && !bucket.fxMissing,
        fxMissing: entries.length > 1 && bucket.fxMissing,
      };
    };

    return Array.from(map.values()).map((e) => {
      const assigned = resolve(e.assigned);
      const adjustments = resolve(e.adjustments);
      const cash = resolve(e.cash);

      // Booking-currency net is always computable from the FX-converted
      // line values. Snap FX float residue so a true zero is not "-0.00".
      const netBcRaw = e.assigned.bc + e.adjustments.bc - e.cash.bc;
      const netBc = Math.abs(netBcRaw) < 0.005 ? 0 : netBcRaw;

      const api = settlementById.get(e.id);
      const settlementCurrency = api?.settlement_currency || null;
      const hasSettlement = !!settlementCurrency
        && Number.isFinite(Number(api?.balance));
      const netSettleRaw = hasSettlement ? Number(api.balance) : netBc;
      const netSettle = Math.abs(netSettleRaw) < 0.005 ? 0 : netSettleRaw;
      const netCurrency = hasSettlement ? settlementCurrency : bookingCur;

      // Only worth showing the equivalent + rate when the units differ.
      const showEquivalent = netCurrency !== bookingCur;
      const currencies = new Set([
        ...e.assigned.byCurrency.keys(),
        ...e.adjustments.byCurrency.keys(),
        ...e.cash.byCurrency.keys(),
      ]);

      // A net across currencies is only meaningful if SOMETHING converted
      // it: either the API's settlement balance, or per-line FX values. On
      // a pre-FX response with mixed currencies both are missing and the
      // booking-currency sum degenerates into raw Ft + EUR (200,000 + 50
      // = "200,050"). Report it as unavailable rather than print a number
      // that is wrong by two orders of magnitude.
      const anyFxMissing = e.assigned.fxMissing
        || e.adjustments.fxMissing
        || e.cash.fxMissing;
      const netAvailable = hasSettlement || currencies.size <= 1 || !anyFxMissing;

      return {
        id: e.id,
        role: e.role,
        name: e.name,
        assigned,
        adjustments,
        cash,
        net: netSettle,
        netCurrency,
        netInBookingCurrency: netBc,
        showEquivalent: showEquivalent && netAvailable,
        netAvailable,
        rate: showEquivalent && netAvailable ? rateFor(netCurrency) : null,
        currencies,
        // True when any column had to leave its native units behind.
        mixed: currencies.size > 1,
      };
    });
  }, [orderLines, operatorById, order.currency, balanceByOperator]);

  // Booking-level profit breakdown. Cash is intentionally NOT subtracted
  // here — cash flows operator↔customer directly and doesn't pass through
  // us; it nets out when computing (what client pays us) − (what we pay
  // operator). All amounts in booking currency via the FX-converted line
  // fields the backend exposes, so multi-currency multi-op bookings
  // still produce a meaningful single profit number.
  const bookingProfit = useMemo(() => {
    const bookingCur = order.currency || 'EUR';
    const bcCredit = (l) => (l.credit_in_booking_currency != null
      ? Number(l.credit_in_booking_currency) : Number(l.credit) || 0);
    const bcDebit = (l) => (l.debit_in_booking_currency != null
      ? Number(l.debit_in_booking_currency) : Number(l.debit) || 0);
    const bcCreditOperator = (l) => (l.credit_operator_in_booking_currency != null
      ? Number(l.credit_operator_in_booking_currency) : Number(l.credit_operator) || 0);

    // Those fallbacks return the RAW figure when the API response predates
    // the FX fields. For a line already in the booking currency that is
    // correct; for a foreign-currency line it silently adds Ft to EUR. A
    // 200,000 Ft driver fee on a EUR booking then produced a profit of
    // -€200,050. Track it per side so the footer can decline to answer
    // instead of printing a number that is wrong ~365x.
    let partnerFxMissing = false;
    let operatorFxMissing = false;
    const noteFx = (l) => {
      if ((l.currency || bookingCur) === bookingCur) return;
      const partnerAmt = (Number(l.debit) || 0) - (Number(l.credit) || 0);
      if (Math.abs(partnerAmt) > 0.005
        && l.debit_in_booking_currency == null
        && l.credit_in_booking_currency == null) partnerFxMissing = true;
      if (Math.abs(Number(l.credit_operator) || 0) > 0.005
        && l.credit_operator_in_booking_currency == null) operatorFxMissing = true;
    };

    let base = 0; // kind: base, service
    let partnerAdj = 0; // kind: partner_cancellation OR adjustment with no operator_id
    let operatorPrice = 0; // kind: operator
    let operatorAdj = 0; // kind: operator_cancellation OR adjustment with operator_id

    orderLines.forEach((l) => {
      noteFx(l);
      // Partner-side accounting: debit = what the partner owes us
      // (revenue), credit = what we credit back (refund/discount).
      // Same convention as `partnerOwed` in the `sides` memo above.
      const partnerAmount = bcDebit(l) - bcCredit(l);
      const opCredit = bcCreditOperator(l);
      switch (l.kind) {
        case 'base':
        case 'service':
          base += partnerAmount;
          break;
        case 'partner_cancellation':
          partnerAdj += partnerAmount;
          break;
        case 'operator':
          operatorPrice += opCredit;
          break;
        case 'operator_cancellation':
          operatorAdj += opCredit;
          break;
        case 'adjustment':
          if (l.metadata?.operator_id) operatorAdj += opCredit;
          else partnerAdj += partnerAmount;
          break;
        // cash_from_client deliberately omitted — see comment above.
        default:
          break;
      }
    });

    // Same FX-residue snap the per-operator summary applies: these sums
    // mix *_in_booking_currency values the backend does not round, so a
    // genuinely-zero figure can land on -1e-13 and render as "€-0.00".
    const snap = (v) => (Math.abs(v) < 0.005 ? 0 : v);
    return {
      base: snap(base),
      partnerAdj: snap(partnerAdj),
      operatorPrice: snap(operatorPrice),
      operatorAdj: snap(operatorAdj),
      profit: snap(base + partnerAdj - operatorPrice - operatorAdj),
      // Each figure is only reportable if its own side converted cleanly;
      // profit spans both sides.
      baseReliable: !partnerFxMissing,
      partnerAdjReliable: !partnerFxMissing,
      profitReliable: !partnerFxMissing && !operatorFxMissing,
    };
  }, [orderLines, order.currency]);

  // Pair each reversal with the line it cancels, so the table renders ONE
  // struck-through row rather than two rows a reader has to net out by hand.
  // The index is the line's ORIGINAL position, which is what the backend fed
  // to getOrderLineKey when it allocated payment lines.
  const { reversalByKey } = useMemo(
    () => pairReversals(orderLines, (line, i) => getOrderLineKey(line, i)),
    [orderLines],
  );

  const visibleKinds = showOnlyAdjustments ? ['adjustment'] : KIND_RENDER_ORDER;

  // Detect the operator settlement currency: scan operator-side lines
  // and check whether they're all in the same currency. If yes, that
  // currency is the natural display unit for the operator-side
  // columns. If mixed (e.g. one driver in HUF + one guide in EUR),
  // there's no single settlement currency to default to and we fall
  // back to the booking currency.
  const operatorSettlementCurrency = useMemo(() => {
    const set = new Set(
      orderLines
        .filter((l) => {
          const opAmount = Number(l.credit_operator) || 0;
          // Magnitude, not sign. Cash-collected lines carry a NEGATIVE
          // credit_operator, so a `> 0.005` test skipped them entirely and
          // they never joined the currency set. On a HUF operator line plus
          // a EUR cash line the set collapsed to {HUF}, the view concluded
          // there was a single settlement currency, and printed the raw
          // -735 EUR as "-735 Ft" — understating the deduction ~364x and
          // making the totals row read 274,265 Ft instead of 7,727 Ft.
          // (PU_DASH_4225290; 174 bookings carry this shape.)
          return Math.abs(opAmount) > 0.005;
        })
        .map((l) => l.currency || bookingCurrency),
    );
    if (set.size === 1) return [...set][0];
    return null;
  }, [orderLines, bookingCurrency]);

  // Effective currency for operator-side columns + panel. When
  // 'settlement' is chosen but no single settlement currency exists
  // (mixed booking), fall through to the booking currency so totals
  // remain meaningful.
  const operatorDisplayCurrency = (operatorCurrencyView === 'settlement' && operatorSettlementCurrency)
    ? operatorSettlementCurrency
    : bookingCurrency;
  const isOperatorViewSettlement = operatorDisplayCurrency !== bookingCurrency;

  // Per-line operator-amount selector — encapsulates the "settlement
  // currency vs booking currency" branch in one place so the table
  // cells + totals row + panel stay consistent.
  const lineCreditOperator = (line) => {
    if (isOperatorViewSettlement) {
      // Show in the line's own currency; the operator-currency totals
      // sum only when ALL operator lines share that currency, which
      // operatorSettlementCurrency guards.
      return Number(line.credit_operator) || 0;
    }
    return line.credit_operator_in_booking_currency != null
      ? Number(line.credit_operator_in_booking_currency)
      : Number(line.credit_operator) || 0;
  };

  // Outstanding summaries — split into TWO independent sides so the
  // partner-side (customer/partner debt) and operator-side (what we
  // owe the operator) reconcile separately.
  //
  // Why two panels: collapsing both sides into a single "Net to PH"
  // number hid the actual partner debt behind a netted figure and was
  // misleading when the operator hadn't been paid yet. Showing both
  // sides keeps each ledger column accountable.
  //
  // Formulas (per side):
  //   still = owed - paid_inbound + paid_outbound
  // Where:
  //   PARTNER  paid_inbound  = sum of direction='in'  payments with kind∈{booking,partner}
  //            paid_outbound = sum of direction='out' (refunds)        with kind∈{booking,partner}
  //   OPERATOR paid_outbound = sum of direction='out' payments with kind=operator
  //            paid_inbound  = sum of direction='in'  (recoupments)    with kind=operator (rare)
  //
  // Mixed-currency payments are excluded from the side sums and a
  // warning is surfaced below — no FX conversion on payments yet (v1).

  const sides = useMemo(() => {
    let partnerInbound = 0;
    let partnerOutbound = 0;
    let operatorInbound = 0;
    let operatorOutbound = 0;
    let hasMixedCurrency = false;
    payments.forEach((p) => {
      if (!p || !SETTLED_PAYMENT_STATUSES.has(p.status)) return;
      if ((p.currency || 'EUR') !== bookingCurrency) {
        hasMixedCurrency = true;
        return;
      }
      const cpKind = p.counterparty?.kind;
      const isPartner = cpKind === 'booking' || cpKind === 'partner';
      const isOperator = cpKind === 'operator';
      if (!isPartner && !isOperator) return;
      // Per-booking-attributable amount. A payment can split across
      // many bookings via lines[]; this booking only owns its share.
      //
      //   - If lines[] has entries that reference THIS booking, sum
      //     only those lines' gross_amount. The other entries belong
      //     to OTHER bookings and must not show up here.
      //   - If lines[] is empty AND counterparty matches this booking
      //     directly (Pattern B — whole-booking allocation), use the
      //     full payment.gross_amount.
      //   - Otherwise the payment doesn't touch this booking; skip.
      //
      // This is the fix for "I split 1344 across two bookings but
      // the Order Ledger on each booking shows 1344 paid in".
      const linesForThisBooking = (p.lines || []).filter(
        (l) => l && l.booking_id === order._id,
      );
      let attributable = 0;
      if (linesForThisBooking.length > 0) {
        attributable = linesForThisBooking.reduce(
          (s, l) => s + (Number(l.gross_amount) || 0),
          0,
        );
      } else if (cpKind === 'booking' && p.counterparty?.id === order._id) {
        attributable = Number(p.gross_amount) || 0;
      }
      if (attributable === 0) return;
      if (isPartner) {
        if (p.direction === 'in') partnerInbound += attributable;
        else if (p.direction === 'out') partnerOutbound += attributable;
      } else if (p.direction === 'out') {
        operatorOutbound += attributable;
      } else if (p.direction === 'in') {
        operatorInbound += attributable;
      }
    });

    // Owed totals from the FX-converted line fields the backend now
    // exposes — operator credits in HUF show up correctly in EUR.
    const lineDebitBC = (l) => (l.debit_in_booking_currency != null
      ? Number(l.debit_in_booking_currency) : Number(l.debit) || 0);
    const lineCreditBC = (l) => (l.credit_in_booking_currency != null
      ? Number(l.credit_in_booking_currency) : Number(l.credit) || 0);
    const partnerOwed = orderLines.reduce(
      (s, l) => s + lineDebitBC(l) - lineCreditBC(l),
      0,
    );
    // Operator-owed total honours the user-selected operator currency
    // view: 'settlement' uses each line's raw `credit_operator` in the
    // operator's currency (valid only when a single settlement
    // currency exists; otherwise the derivation forces 'booking'),
    // 'booking' uses the FX-converted value.
    const operatorOwed = orderLines.reduce(
      (s, l) => s + lineCreditOperator(l),
      0,
    );

    return {
      partner: {
        owed: partnerOwed,
        inbound: partnerInbound,
        outbound: partnerOutbound,
        still: partnerOwed - partnerInbound + partnerOutbound,
      },
      operator: {
        owed: operatorOwed,
        inbound: operatorInbound,
        outbound: operatorOutbound,
        still: operatorOwed - operatorOutbound + operatorInbound,
      },
      hasMixedCurrency,
    };
    // `operatorCurrencyView` IS a dependency even though the memo body
    // reads it through the `lineCreditOperator` closure — without it
    // here, the toggle would just flip the column label and leave the
    // cached sums stale, so the operator-side panel kept showing the
    // same number ("Owed 73,980 EUR") in both views.
  }, [payments, orderLines, bookingCurrency, operatorCurrencyView]);

  // Profit sign → color. Positive (we made money) is green; zero is
  // muted; negative (we lost money) is red.
  const profitColorFor = (profit) => {
    if (profit > 0.005) return 'text-success';
    if (profit < -0.005) return 'text-danger';
    return '';
  };

  // "still" sign → color. Positive (debt remaining) is red; zero is
  // green (settled); negative is "we've over-collected / over-paid".
  // Tolerance covers 2dp float noise.
  const sideColorFor = (still) => {
    if (still > 0.005) return 'text-danger';
    if (still < -0.005) return 'text-primary';
    return 'text-success';
  };

  // Per-order_line paid amounts, split into partner and operator sides.
  //
  // Split logic — the two sides are tracked as independent columns now
  // (no more netting). A line key can have BOTH a partner-side paid
  // total AND an operator-side paid total simultaneously when both
  // flows touched it.
  //
  // Partner-side (the money the customer/partner owes us):
  //   - payment.counterparty.kind ∈ {'booking', 'partner'}
  //   - direction='in' adds, direction='out' subtracts (refund offset)
  //
  // Operator-side (the money we owe the operator):
  //   - payment.counterparty.kind === 'operator'
  //   - direction='out' adds (payout to operator), direction='in'
  //     subtracts (operator paying us back / cash recovery)
  //
  // Only settled payments (SETTLED_PAYMENT_STATUSES — completed,
  // reconciled AND refunded; see Utils/paymentStatus.js for why a
  // refunded original must stay in the sum) with an explicit
  // order_line_key are counted. Payments without an order_line_key, or
  // whole-booking Pattern B payments with no lines[], fall through to
  // the booking-global footer sums below.
  const { paidPartnerByKey, paidOperatorByKey } = useMemo(() => {
    const partnerMap = new Map();
    const operatorMap = new Map();
    payments.forEach((p) => {
      if (!p || !SETTLED_PAYMENT_STATUSES.has(p.status)) return;
      const cpKind = p.counterparty?.kind;
      const isPartner = cpKind === 'booking' || cpKind === 'partner';
      const isOperator = cpKind === 'operator';
      if (!isPartner && !isOperator) return;
      (p.lines || []).forEach((l) => {
        if (!l || l.booking_id !== order._id || !l.order_line_key) return;
        const amt = Number(l.gross_amount) || 0;
        if (isPartner) {
          // Partner side: 'in' is what we received, 'out' is a refund.
          const sign = p.direction === 'in' ? 1 : -1;
          partnerMap.set(
            l.order_line_key,
            (partnerMap.get(l.order_line_key) || 0) + sign * amt,
          );
        } else if (isOperator) {
          // Operator side: 'out' is the payout, 'in' offsets it.
          const sign = p.direction === 'out' ? 1 : -1;
          operatorMap.set(
            l.order_line_key,
            (operatorMap.get(l.order_line_key) || 0) + sign * amt,
          );
        }
      });
    });
    return { paidPartnerByKey: partnerMap, paidOperatorByKey: operatorMap };
  }, [payments, order._id]);

  // Sum of payment amounts that COULD NOT be attributed to a specific
  // order_line — either:
  //   - Payment.lines[] entry for this booking has no order_line_key
  //     (legacy data pre-backfill, or partner/operator multi-booking
  //     allocations that intentionally don't pin to a line), OR
  //   - the Payment itself has no lines[] and its counterparty IS this
  //     booking (whole-booking allocation, e.g. a single wire that
  //     settled the booking before line-level attribution existed).
  // Split by side using payment.counterparty.kind so the partner and
  // operator footer columns each reconcile independently.
  const { bookingGlobalPaidPartner, bookingGlobalPaidOperator } = useMemo(() => {
    let partner = 0;
    let operator = 0;
    payments.forEach((p) => {
      if (!p || !SETTLED_PAYMENT_STATUSES.has(p.status)) return;
      const cpKind = p.counterparty?.kind;
      const isPartner = cpKind === 'booking' || cpKind === 'partner';
      const isOperator = cpKind === 'operator';
      if (!isPartner && !isOperator) return;
      const partnerSign = p.direction === 'in' ? 1 : -1;
      const operatorSign = p.direction === 'out' ? 1 : -1;
      const hasLines = Array.isArray(p.lines) && p.lines.length > 0;
      // Pattern B: whole-booking allocation with no lines[]. Only
      // applies to booking-counterparty payments — operator payouts
      // always have lines[].
      if (
        !hasLines
        && cpKind === 'booking'
        && p.counterparty?.id === order._id
      ) {
        partner += partnerSign * (Number(p.gross_amount) || 0);
        return;
      }
      (p.lines || []).forEach((l) => {
        if (!l || l.booking_id !== order._id) return;
        if (l.order_line_key) return; // attributed — handled per-line
        const amt = Number(l.gross_amount) || 0;
        if (isPartner) partner += partnerSign * amt;
        else if (isOperator) operator += operatorSign * amt;
      });
    });
    return {
      bookingGlobalPaidPartner: partner,
      bookingGlobalPaidOperator: operator,
    };
  }, [payments, order._id]);

  // Color helper for the per-line outstanding cell — matches the
  // booking-global Outstanding panel's tolerance + palette so the two
  // surfaces read consistently.
  const outstandingColorFor = (value) => {
    if (value > 0.005) return 'text-danger';
    if (value < -0.005) return 'text-primary';
    return 'text-success';
  };

  // A paid / outstanding figure, once the payments are known; until then a
  // placeholder, never a €0.00 that reads as "nothing paid". '—' for a side
  // with no money on the line at all.
  const paidKnown = loadedPayments !== null;
  const notKnown = <span className="text-muted" title="Waiting for the payments">…</span>;
  const paidCell = (value, currency, has = true) => {
    if (!has) return '—';
    return paidKnown ? formatMoney(value, currency) : notKnown;
  };
  const outstandingClass = (value, has = true) => (
    has && paidKnown ? outstandingColorFor(value) : ''
  );

  const renderLineRow = (line, index) => {
    const meta = KIND_META[line.kind] || KIND_META.adjustment;
    let label = line.description;
    if (line.kind === 'operator') label = labelForOperatorLine(line) || line.description;
    else if (line.kind === 'cash_from_client') label = `${labelForOperatorLine(line) || ''} — cash`.trim();
    else if (line.kind === 'service') label = labelForServiceLine(line) || line.description;

    // Per-line partner / operator split. `index` is the line's ORIGINAL
    // index in orderLines (passed down from the group iteration) so the
    // key matches what the backend computed when allocating payment
    // lines.
    //
    // Partner-side owed = debit − credit (what the customer/partner
    //   still owes; ignores anything we owe out to the operator).
    // Operator-side owed = credit_operator (what we owe the operator).
    //
    // The two sides are independent — operator/cash_from_client lines
    // typically only have an operator-side, while base/service/discount
    // lines only have a partner-side. When both sides on a line are
    // zero we render an empty cell so the table stays scannable.
    const lineKey = getOrderLineKey(line, index);

    // Partner-side "owed" uses the FX-converted-to-booking-currency
    // fields the backend computed. Falls back to raw debit/credit when
    // the field is missing (older API responses without conversion) —
    // safe for the common all-EUR case where converted == raw.
    const debitBC = line.debit_in_booking_currency != null
      ? Number(line.debit_in_booking_currency)
      : Number(line.debit) || 0;
    const creditBC = line.credit_in_booking_currency != null
      ? Number(line.credit_in_booking_currency)
      : Number(line.credit) || 0;

    const owedPartner = debitBC - creditBC;
    const paidPartner = paidPartnerByKey.get(lineKey) || 0;
    const outstandingPartner = owedPartner - paidPartner;
    const hasPartner = Math.abs(owedPartner) > 0.005
      || Math.abs(paidPartner) > 0.005;

    // Operator-side: this is where the HUF-as-EUR bug hides. Using the
    // FX-converted value means a 73,980 HUF operator share displays as
    // ~€185, not "€73,980". paidOperator currently sums payment lines
    // by lineKey without FX conversion — if/when we route operator
    // payouts in non-booking currencies through this view, we'll want
    // to convert there too. Today that path is rare enough that
    // documenting it here is sufficient.
    const owedOperator = lineCreditOperator(line);
    const paidOperator = paidOperatorByKey.get(lineKey) || 0;
    const outstandingOperator = owedOperator - paidOperator;
    const hasOperator = Math.abs(owedOperator) > 0.005
      || Math.abs(paidOperator) > 0.005;

    const cellCurrency = order.currency || 'EUR';
    // Operator-side cells follow the user-chosen view ('settlement'
    // shows the line's own currency, 'booking' shows the FX-converted
    // value in booking currency). When the booking has a mix of
    // operator-line currencies, settlement view collapses back to
    // booking currency (see operatorSettlementCurrency derivation).
    const operatorCellCurrency = operatorDisplayCurrency;

    // ── what this row may offer ──────────────────────────────────────────
    // An adjustment is editable and deletable unless a payment or an invoice
    // references it. That question is answered ONLY by the lock map the API
    // builds from Payment.lines[] and Invoice.lines[] — never by the
    // booking's status, which would freeze every safe line on a completed
    // booking and free a paid line on a confirmed one.
    const isAdjustment = (line.kind || 'adjustment') === 'adjustment';
    const lineLocks = locksForLine(locks, lineKey);
    const isLocked = lineLocks.length > 0;
    const reversal = reversalByKey.get(lineKey);
    const isReversed = !!reversal;
    const canAct = isAdjustment && !!line._id;

    return (
      <tr
        key={line._index}
        data-testid={isAdjustment ? `ledger-row-${line._id || line._index}` : undefined}
        className={isReversed ? 'text-muted' : undefined}
      >
        <td>
          <Badge bg={meta.variant} className="me-1">
            <i className={`fa ${meta.icon} me-1`} />
            {meta.label}
          </Badge>
          {meta.readOnly && (
            <small className="text-muted">auto</small>
          )}
          {isLocked && isAdjustment && (
            <i
              className="fa fa-lock ms-1 text-secondary"
              data-testid={`line-locked-${line._id || line._index}`}
              title={describeLocks(lineLocks)}
              aria-label={describeLocks(lineLocks)}
            />
          )}
        </td>
        <td className="small">
          {/* A reversed line and its paired negative are ONE row: struck
              through, with the reason beside it. Rendered as two rows the
              reader had to spot that they cancel and guess why. */}
          <span style={isReversed ? { textDecoration: 'line-through' } : undefined}>
            {label}
          </span>
          {isReversed && (
            <span
              className="text-danger ms-2 small"
              data-testid={`line-reversed-${line._id || line._index}`}
            >
              {`— reversed${reversalReason(reversal.line) ? ` — ${reversalReason(reversal.line)}` : ''}`}
            </span>
          )}
        </td>
        <td className="text-end">{line.debit ? formatMoney(line.debit, line.currency) : ''}</td>
        <td className="text-end">{line.credit ? formatMoney(line.credit, line.currency) : ''}</td>
        <td className="text-end">
          {line.credit_operator
            ? formatMoney(line.credit_operator, line.currency)
            : ''}
        </td>
        <td className="text-end small">
          {paidCell(paidPartner, cellCurrency, hasPartner)}
        </td>
        <td className={`text-end small fw-bold ${outstandingClass(outstandingPartner, hasPartner)}`}>
          {paidCell(outstandingPartner, cellCurrency, hasPartner)}
        </td>
        <td className="text-end small">
          {paidCell(paidOperator, operatorCellCurrency, hasOperator)}
        </td>
        <td className={`text-end small fw-bold ${outstandingClass(outstandingOperator, hasOperator)}`}>
          {paidCell(outstandingOperator, operatorCellCurrency, hasOperator)}
        </td>
        {renderActions && (
          <td className="text-end text-nowrap">
            {canAct && !isReversed && renderActions(line, lineLocks)}
          </td>
        )}
      </tr>
    );
  };

  // Kind, description, three amounts, paid/outstanding per side, actions.
  const columns = renderActions ? 10 : 9;

  return (
    <>
      {/* Per-operator summary panel */}
      {perOperator.length > 0 && (
      <Card className="mb-3">
        <Card.Header>
          <i className="fa fa-users me-2" />
          Per-operator summary
        </Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Operator</th>
                <th className="text-end">Assigned</th>
                <th className="text-end">Adjustments</th>
                <th className="text-end">Cash collected</th>
                <th className="text-end">Net to settle</th>
                <th>Currency</th>
              </tr>
            </thead>
            <tbody>
              {perOperator.map((op) => (
                <tr key={op.id}>
                  <td>
                    {op.name || `Operator ${op.id.slice(0, 6)}`}
                    {op.role && (
                      <Badge bg="light" text="dark" className="ms-2">
                        {formatRole(op.role)}
                      </Badge>
                    )}
                  </td>
                  <td className="text-end">
                    {Math.abs(op.assigned.amount) > 0.005
                      ? formatMoney(op.assigned.amount, op.assigned.currency)
                      : '—'}
                    {op.assigned.converted && (
                      <div className="text-muted small">converted</div>
                    )}
                  </td>
                  <td className="text-end">
                    {Math.abs(op.adjustments.amount) > 0.005
                      ? `${op.adjustments.amount > 0 ? '+ ' : '- '}${formatMoney(Math.abs(op.adjustments.amount), op.adjustments.currency)}`
                      : '—'}
                    {op.adjustments.converted && (
                      <div className="text-muted small">converted</div>
                    )}
                  </td>
                  <td className="text-end">
                    {op.cash.amount > 0.005
                      ? `- ${formatMoney(op.cash.amount, op.cash.currency)}`
                      : '—'}
                  </td>
                  <td className="text-end fw-bold">
                    {op.netAvailable
                      ? formatMoney(op.net, op.netCurrency)
                      : <span className="text-danger">no FX rate</span>}
                    {/* Ft + EUR cannot be added, so the net is the one
                        converted figure. Show the booking-currency
                        equivalent under it so both units are on screen. */}
                    {op.showEquivalent && (
                      <div className="text-muted small fw-normal">
                        {`(${formatMoney(op.netInBookingCurrency, bookingCurrency)})`}
                      </div>
                    )}
                  </td>
                  <td>
                    {op.netCurrency}
                    {op.showEquivalent && op.rate && (
                      <div
                        className="text-muted small"
                        title={'Booking-date rate the API applied when converting this booking\'s lines (anchored to the pickup date, not today).'}
                      >
                        {`@${op.rate.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${op.netCurrency}/${bookingCurrency}`}
                      </div>
                    )}
                    {op.mixed && (
                      <Badge
                        bg="warning"
                        text="dark"
                        className="ms-2"
                        title={`This operator's lines mix ${[...op.currencies].join(' + ')}. Each column is shown in the currency it was agreed in; only the net is converted.`}
                      >
                        mixed
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Booking-level profit footer. Math lives in
                bookingProfit (booking currency, FX-converted). Cash
                intentionally not subtracted — it nets out between
                client→operator direct and our settlement. */}
            <tfoot>
              <tr style={{ borderTop: '2px solid #dee2e6' }}>
                <td className="text-muted">Base price (booking)</td>
                <td colSpan="3" />
                <td className="text-end">
                  {bookingProfit.baseReliable
                    ? `+ ${formatMoney(bookingProfit.base, bookingCurrency)}`
                    : <span className="text-danger">no FX rate</span>}
                </td>
                <td className="text-muted">{bookingCurrency}</td>
              </tr>
              <tr>
                <td className="text-muted">Partner adjustments</td>
                <td colSpan="3" />
                <td className="text-end">
                  {bookingProfit.partnerAdjReliable
                    ? `${bookingProfit.partnerAdj >= 0 ? '+ ' : '- '}${formatMoney(Math.abs(bookingProfit.partnerAdj), bookingCurrency)}`
                    : <span className="text-danger">no FX rate</span>}
                </td>
                <td className="text-muted">{bookingCurrency}</td>
              </tr>
              <tr style={{ borderTop: '2px solid #dee2e6' }}>
                <td className="fw-bold">Our profit</td>
                <td colSpan="3" className="text-muted small">
                  base + partner adj − operator price − operator adj
                </td>
                <td className={`text-end fw-bold ${bookingProfit.profitReliable ? profitColorFor(bookingProfit.profit) : ''}`}>
                  {bookingProfit.profitReliable
                    ? formatMoney(bookingProfit.profit, bookingCurrency)
                    : <span className="text-danger">no FX rate</span>}
                </td>
                <td className="text-muted">{bookingCurrency}</td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>
      )}

      <Row className="align-items-center mb-2">
        <Col>
          <strong>Ledger lines</strong>
          <small className="text-muted ms-2">
            Auto rows are server-generated from the booking. Only adjustments are editable.
          </small>
        </Col>
        <Col xs="auto" className="d-flex align-items-center gap-3">
          {/* Operator-side currency view toggle — only renders when
              there's a single operator settlement currency to flip
              to. Multi-currency operator bookings stay locked to
              the booking currency (FX-converted) since there's no
              unambiguous "the operator's currency" to switch to. */}
          {operatorSettlementCurrency
            && operatorSettlementCurrency !== bookingCurrency && (
            <div className="d-flex align-items-center gap-1">
              <small className="text-muted">Operator side</small>
              <Button
                size="sm"
                variant={operatorCurrencyView === 'settlement' ? 'primary' : 'outline-secondary'}
                onClick={() => setOperatorCurrencyView('settlement')}
                data-testid="op-currency-settlement"
                title={`Show operator-side amounts in ${operatorSettlementCurrency}`}
              >
                {operatorSettlementCurrency}
              </Button>
              <Button
                size="sm"
                variant={operatorCurrencyView === 'booking' ? 'primary' : 'outline-secondary'}
                onClick={() => setOperatorCurrencyView('booking')}
                data-testid="op-currency-booking"
                title={`Show operator-side amounts in ${bookingCurrency} (FX-converted)`}
              >
                {bookingCurrency}
              </Button>
            </div>
          )}
          <Form.Check
            type="switch"
            id="adjustments-only-toggle"
            label="Show adjustments only"
            checked={showOnlyAdjustments}
            onChange={(e) => setShowOnlyAdjustments(e.target.checked)}
          />
        </Col>
      </Row>

      <Table responsive striped bordered hover className="mt-2 align-middle">
        <thead>
          <tr>
            <th style={{ width: 220 }}>Kind</th>
            <th>Description</th>
            <th className="text-end">Debit</th>
            <th className="text-end">Credit</th>
            <th className="text-end">Credit operator</th>
            <th className="text-end" style={{ width: 120 }}>
              Paid (partner,
              {' '}
              {order.currency || 'EUR'}
              )
            </th>
            <th className="text-end" style={{ width: 130 }}>
              Outstanding (partner,
              {' '}
              {order.currency || 'EUR'}
              )
            </th>
            <th className="text-end" style={{ width: 120 }}>
              Paid (operator,
              {' '}
              {operatorDisplayCurrency}
              )
            </th>
            <th className="text-end" style={{ width: 130 }}>
              Outstanding (operator,
              {' '}
              {operatorDisplayCurrency}
              )
            </th>
            {renderActions && <th className="text-end" style={{ width: 120 }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {visibleKinds.map((kind) => {
            const lines = groups.get(kind);
            if (!lines || lines.length === 0) return null;
            // Use `line._index` (the original index within
            // orderLines) for the key so getOrderLineKey returns the
            // same value the backend computed when allocating
            // payment lines.
            return lines
              // A reversal is rendered INSIDE the row it cancels, so it
              // gets no row of its own. Two rows that happen to cancel
              // is the shape this work exists to remove.
              .filter((line) => !isReversalLine(line))
              .map((line) => renderLineRow(line, line._index));
          })}

          {!showOnlyAdjustments && orderLines.length === 0 && (
          <tr>
            <td colSpan={columns} className="text-center text-muted py-3">
              No ledger lines yet.
            </td>
          </tr>
          )}
        </tbody>
        <tfoot>
          {(() => {
            // Per-column totals — two independent sums per side, NOT
            // netted against each other. Operator-side outstanding
            // is *only* what we owe the operator; partner-side
            // outstanding is *only* what the partner owes us.
            //
            // We compute owed totals from the raw orderLines (not
            // totals.balance, which is a netted "company position"
            // and was the source of the original conflation bug).
            //
            // Use the FX-converted-to-booking-currency fields so
            // operator lines in HUF don't leak their raw HUF
            // amounts into a column labelled EUR. Fall back to the
            // raw field for older API responses.
            const lineDebitBC = (l) => (l.debit_in_booking_currency != null
              ? Number(l.debit_in_booking_currency)
              : Number(l.debit) || 0);
            const lineCreditBC = (l) => (l.credit_in_booking_currency != null
              ? Number(l.credit_in_booking_currency)
              : Number(l.credit) || 0);
            const totalOwedPartner = orderLines.reduce(
              (s, l) => s + lineDebitBC(l) - lineCreditBC(l),
              0,
            );
            // Operator-side totals follow the user-chosen currency
            // view (settlement vs booking) via lineCreditOperator
            // — so a "show in HUF" view sums Ft per line, while
            // "show in EUR" uses the FX-converted values.
            const totalOwedOperator = orderLines.reduce(
              (s, l) => s + lineCreditOperator(l),
              0,
            );
            const totalPaidPartner = Array.from(paidPartnerByKey.values())
              .reduce((s, v) => s + v, 0);
            const totalPaidOperator = Array.from(paidOperatorByKey.values())
              .reduce((s, v) => s + v, 0);
            const totalOutstandingPartner = totalOwedPartner - totalPaidPartner;
            const totalOutstandingOperator = totalOwedOperator - totalPaidOperator;
            return (
              <tr>
                <td colSpan={5} className="text-end fw-bold">Totals</td>
                <td className="text-end fw-bold">
                  {paidCell(totalPaidPartner, order.currency || 'EUR')}
                </td>
                <td className={`text-end fw-bold ${outstandingClass(totalOutstandingPartner)}`}>
                  {paidCell(totalOutstandingPartner, order.currency || 'EUR')}
                </td>
                <td className="text-end fw-bold">
                  {paidCell(totalPaidOperator, operatorDisplayCurrency)}
                </td>
                <td className={`text-end fw-bold ${outstandingClass(totalOutstandingOperator)}`}>
                  {paidCell(totalOutstandingOperator, operatorDisplayCurrency)}
                </td>
                {renderActions && <td />}
              </tr>
            );
          })()}
          {/* Booking-global allocations row — payments that landed
              on the booking but weren't attributed to a specific
              order_line (legacy data, or pattern-B counterparty
              allocations with no lines[]). Split by side so each
              column reconciles independently. Hidden when both
              sides are zero to keep the table tidy. */}
          {(Math.abs(bookingGlobalPaidPartner) > 0.005
            || Math.abs(bookingGlobalPaidOperator) > 0.005) && (
            <tr>
              <td colSpan={5} className="text-end text-muted small">
                Booking-global allocations
              </td>
              <td className="text-end text-muted small">
                {Math.abs(bookingGlobalPaidPartner) > 0.005
                  ? formatMoney(bookingGlobalPaidPartner, order.currency || 'EUR')
                  : '—'}
              </td>
              <td className="text-end text-muted small">—</td>
              <td className="text-end text-muted small">
                {Math.abs(bookingGlobalPaidOperator) > 0.005
                  ? formatMoney(bookingGlobalPaidOperator, order.currency || 'EUR')
                  : '—'}
              </td>
              <td className="text-end text-muted small">—</td>
              {renderActions && <td />}
            </tr>
          )}
        </tfoot>
      </Table>

      {/* Two-sided outstanding — partner and operator reconcile
        independently so an operator we haven't paid yet doesn't
        net against partner debt the customer hasn't paid yet.
        Sits BETWEEN the per-line totals and the payments section
        so the formula reads top-to-bottom. v1: same-currency
        payments only; mixed-currency are excluded with a warning. */}
      {loadedPayments && (
      <Row className="mt-3 g-2">
        <Col md={6}>
          <Card className="h-100">
            <Card.Body className="py-2">
              <div className="text-muted small mb-1">
                <strong>Partner side</strong>
                {' '}
                — customer / partner owes us
              </div>
              <div className="d-flex justify-content-between small">
                <span>Owed</span>
                <strong>{formatMoney(sides.partner.owed, bookingCurrency)}</strong>
              </div>
              <div className="d-flex justify-content-between small text-muted">
                <span>− Paid in</span>
                <span>{formatMoney(sides.partner.inbound, bookingCurrency)}</span>
              </div>
              <div className="d-flex justify-content-between small text-muted">
                <span>+ Refunded out</span>
                <span>{formatMoney(sides.partner.outbound, bookingCurrency)}</span>
              </div>
              <hr className="my-1" />
              <div className="d-flex justify-content-between">
                <span>
                  <strong>Still due</strong>
                </span>
                <span className={`fw-bold fs-5 ${sideColorFor(sides.partner.still)}`}>
                  {formatMoney(sides.partner.still, bookingCurrency)}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100">
            <Card.Body className="py-2">
              <div className="text-muted small mb-1">
                <strong>Operator side</strong>
                {' '}
                — we owe the operator
              </div>
              <div className="d-flex justify-content-between small">
                <span>Owed</span>
                <strong>{formatMoney(sides.operator.owed, operatorDisplayCurrency)}</strong>
              </div>
              <div className="d-flex justify-content-between small text-muted">
                <span>− Paid out</span>
                <span>{formatMoney(sides.operator.outbound, operatorDisplayCurrency)}</span>
              </div>
              <div className="d-flex justify-content-between small text-muted">
                <span>+ Recouped in</span>
                <span>{formatMoney(sides.operator.inbound, operatorDisplayCurrency)}</span>
              </div>
              <hr className="my-1" />
              <div className="d-flex justify-content-between">
                <span>
                  <strong>Still to pay</strong>
                </span>
                <span className={`fw-bold fs-5 ${sideColorFor(sides.operator.still)}`}>
                  {formatMoney(sides.operator.still, operatorDisplayCurrency)}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        {sides.hasMixedCurrency && (
          <Col md={12}>
            <div className="text-warning small mt-1">
              <i className="fa fa-exclamation-triangle me-1" />
              Some payments are in a different currency — totals shown
              in booking currency, no FX conversion applied to payments.
            </div>
          </Col>
        )}
      </Row>
      )}
    </>
  );
}

export default OrderLedger;
